import { Component } from '@theme/component';

/** Image slideshow with editor-aware lifecycle, pointer gestures and optional autoplay. */
class GenericHeroCarousel extends Component {
  requiredRefs = ['viewport', 'track'];
  #controller;
  #observer;
  #timer;
  #frame;
  #index = 0;
  #pointer;
  #suppressClick = false;
  #hovered = false;
  #focused = false;
  #editorSelected = false;
  #editorBlockId;
  #userPaused = false;
  #motion;

  connectedCallback() {
    super.connectedCallback();
    this.#index = Number(this.dataset.initialSlide) || 0;
    this.#setup();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#setup();
  }

  disconnectedCallback() {
    this.#cleanup();
    super.disconnectedCallback();
  }

  #setup() {
    this.#cleanup();
    this.#controller = new AbortController();
    const signal = this.#controller.signal;
    const viewport = this.refs.viewport;
    if (!(viewport instanceof HTMLElement) || !(this.refs.track instanceof HTMLElement)) return;
    this.#motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.#hovered = false;
    this.#focused = this.contains(document.activeElement);
    this.#editorSelected = this.#slides.some(slide => slide.dataset.blockId === this.#editorBlockId);
    this.#pointer = undefined;
    this.#suppressClick = false;
    const listen = (node, event, listener, options = {}) => node?.addEventListener(event, listener, { ...options, signal });
    listen(this.refs.previous, 'click', () => this.#go(this.#index - 1));
    listen(this.refs.next, 'click', () => this.#go(this.#index + 1));
    for (const dot of this.refs.dots ?? []) listen(dot, 'click', () => this.#go(Number(dot.dataset.slideIndex)));
    listen(this.refs.pause, 'click', () => { this.#userPaused = !this.#userPaused; this.#schedule(); });
    listen(viewport, 'pointerdown', event => {
      if (!event.isPrimary || event.button > 0 || this.#slides.length < 2) return;
      this.#suppressClick = false;
      this.#pointer = { id: event.pointerId, x: event.clientX, y: event.clientY, delta: 0, dragging: false };
      this.#schedule();
    });
    listen(viewport, 'pointermove', event => {
      const pointer = this.#pointer;
      if (!pointer || pointer.id !== event.pointerId) return;
      const delta = event.clientX - pointer.x;
      const vertical = event.clientY - pointer.y;
      if (!pointer.dragging) {
        if (Math.abs(vertical) > 8 && Math.abs(vertical) > Math.abs(delta)) { this.#pointer = undefined; this.#schedule(); return; }
        if (Math.abs(delta) < 8) return;
        pointer.dragging = true;
        viewport.setPointerCapture(event.pointerId);
        this.classList.add('is-dragging');
      }
      pointer.delta = delta;
      const forward = this.#rtl ? delta : -delta;
      const atEdge = !this.#loop && ((this.#index === 0 && forward < 0) || (this.#index === this.#slides.length - 1 && forward > 0));
      this.refs.track.style.transition = 'none';
      this.refs.track.style.transform = `translate3d(${-this.#offset(this.#index) + delta * (atEdge ? 0.25 : 1)}px,0,0)`;
    });
    listen(viewport, 'pointerup', event => this.#finishGesture(event, false));
    listen(viewport, 'pointercancel', event => this.#finishGesture(event, true));
    listen(document, 'pointerup', event => this.#finishGesture(event, false));
    listen(document, 'pointercancel', event => this.#finishGesture(event, true));
    listen(viewport, 'click', event => {
      if (!this.#suppressClick) return;
      event.preventDefault(); event.stopPropagation(); this.#suppressClick = false;
    }, { capture: true });
    listen(this, 'dragstart', event => event.preventDefault());
    listen(this, 'pointerenter', event => { if (event.pointerType === 'mouse') { this.#hovered = true; this.#schedule(); } });
    listen(this, 'pointerleave', () => { this.#hovered = false; this.#schedule(); });
    listen(this, 'focusin', () => { this.#focused = true; this.#schedule(); });
    listen(this, 'focusout', event => { this.#focused = this.contains(event.relatedTarget); this.#schedule(); });
    listen(this, 'keydown', event => {
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) return;
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
        const direction = event.key === 'ArrowRight' ? 1 : -1;
        this.#go(this.#index + direction * (this.#rtl ? -1 : 1));
      }
    });
    listen(document, 'visibilitychange', () => this.#schedule());
    listen(viewport, 'load', () => this.#syncHeight(), { capture: true });
    listen(this.#motion, 'change', () => { this.#go(this.#index, false); });
    listen(document, 'shopify:block:select', event => {
      const slide = this.#slides.find(item => item.dataset.blockId === event.detail?.blockId);
      if (!slide) return;
      this.#editorBlockId = event.detail.blockId;
      this.#editorSelected = true;
      this.#go(this.#slides.indexOf(slide));
    });
    listen(document, 'shopify:block:deselect', event => {
      if (!this.#slides.some(item => item.dataset.blockId === event.detail?.blockId)) return;
      this.#editorBlockId = undefined;
      this.#editorSelected = false; this.#schedule();
    });
    let viewportWidth = viewport.getBoundingClientRect().width;
    this.#observer = new ResizeObserver(() => {
      cancelAnimationFrame(this.#frame);
      this.#frame = requestAnimationFrame(() => {
        if (!this.isConnected) return;
        const nextWidth = viewport.getBoundingClientRect().width;
        if (Math.abs(nextWidth - viewportWidth) > 0.01) {
          viewportWidth = nextWidth;
          this.#go(this.#index, false);
        } else {
          this.#syncHeight();
        }
      });
    });
    this.#observer.observe(viewport);
    for (const slide of this.#slides) this.#observer.observe(slide);
    viewport.scrollLeft = 0;
    this.dataset.ready = '';
    this.#go(this.#index, false);
  }

  get #slides() { return this.refs.slides ?? []; }
  get #loop() { return this.dataset.loop === 'true'; }
  get #rtl() { return getComputedStyle(this).direction === 'rtl'; }
  #offset(index) { return (this.#slides[index]?.offsetLeft ?? 0) - (this.#slides[0]?.offsetLeft ?? 0); }

  #go(nextIndex, animate = true) {
    const count = this.#slides.length;
    if (!count || !(this.refs.track instanceof HTMLElement)) return;
    this.#index = this.#loop ? ((nextIndex % count) + count) % count : Math.min(Math.max(nextIndex, 0), count - 1);
    const duration = this.#motion?.matches ? 0 : Math.max(Number(this.dataset.transitionMs) || 0, 0);
    this.refs.track.style.transition = animate ? `transform ${duration}ms ease` : 'none';
    this.refs.track.style.transform = `translate3d(${-this.#offset(this.#index)}px,0,0)`;
    this.#syncHeight();
    if (this.#slides.some((slide, index) => index !== this.#index && slide.contains(document.activeElement))) {
      this.refs.viewport.focus({ preventScroll: true });
    }
    this.#slides.forEach((slide, index) => {
      const active = index === this.#index;
      slide.inert = !active;
      slide.setAttribute('aria-hidden', String(!active));
    });
    (this.refs.dots ?? []).forEach((dot, index) => {
      dot.classList.toggle('is-active', index === this.#index);
      dot.setAttribute('aria-current', String(index === this.#index));
    });
    if (this.refs.previous) this.refs.previous.disabled = !this.#loop && this.#index === 0;
    if (this.refs.next) this.refs.next.disabled = !this.#loop && this.#index === count - 1;
    this.#schedule();
  }

  #syncHeight() {
    const viewport = this.refs.viewport;
    if (!(viewport instanceof HTMLElement)) return;
    if (this.dataset.heightMode === 'fixed') {
      viewport.style.removeProperty('height');
      return;
    }
    const height = this.#slides[this.#index]?.getBoundingClientRect().height;
    if (height > 0 && Math.abs((parseFloat(viewport.style.height) || 0) - height) > 0.01) {
      viewport.style.height = `${height}px`;
    }
  }

  #finishGesture(event, cancelled) {
    const pointer = this.#pointer;
    if (!pointer || pointer.id !== event.pointerId) return;
    this.#pointer = undefined;
    if (this.refs.viewport.hasPointerCapture(event.pointerId)) this.refs.viewport.releasePointerCapture(event.pointerId);
    this.classList.remove('is-dragging');
    this.#suppressClick = pointer.dragging && !cancelled;
    if (pointer.dragging && !cancelled && Math.abs(pointer.delta) > Math.max(this.refs.viewport.clientWidth * 0.12, 48)) {
      this.#go(this.#index + (pointer.delta < 0 ? 1 : -1) * (this.#rtl ? -1 : 1));
    } else this.#go(this.#index);
  }

  #schedule() {
    clearTimeout(this.#timer);
    if (this.refs.pause) {
      const paused = this.#userPaused || !!this.#motion?.matches;
      this.refs.pause.setAttribute('aria-pressed', String(paused));
      this.refs.pause.setAttribute('aria-label', paused ? this.refs.pause.dataset.playLabel : this.refs.pause.dataset.pauseLabel);
      this.refs.pause.disabled = !!this.#motion?.matches;
    }
    if (!this.isConnected || this.dataset.autoplay !== 'true' || this.#slides.length < 2 || this.#userPaused || this.#hovered || this.#focused || this.#pointer || this.#editorSelected || document.hidden || this.#motion?.matches) return;
    if (!this.#loop && this.#index === this.#slides.length - 1) return;
    this.#timer = setTimeout(() => this.#go(this.#index + 1), Math.max(Number(this.dataset.autoplayInterval) || 5, 2) * 1000);
  }

  #cleanup() {
    this.#controller?.abort();
    this.#observer?.disconnect();
    clearTimeout(this.#timer);
    cancelAnimationFrame(this.#frame);
    this.classList.remove('is-dragging');
  }
}

if (!customElements.get('generic-hero-carousel')) customElements.define('generic-hero-carousel', GenericHeroCarousel);
